import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Heart, AlertCircle, Home, Mail } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";

// Native "Am I Ready to Foster?" quiz — 10 questions, weighted scoring
const QUESTIONS = [
  {
    q: "Do you have a stable living situation (housing, employment, income)?",
    help: "Foster agencies require verifiable stability — doesn't need to be wealthy, just steady.",
    options: [
      { t: "Yes — stable for 2+ years", w: 3 },
      { t: "Mostly stable", w: 2 },
      { t: "Working on it", w: 1 },
      { t: "Not currently", w: 0 },
    ],
  },
  {
    q: "How would you describe your emotional bandwidth right now?",
    help: "Foster children often arrive with trauma. You'll need patience and calm on hard days.",
    options: [
      { t: "Strong — I can regulate through hard days", w: 3 },
      { t: "Good most of the time", w: 2 },
      { t: "It varies a lot", w: 1 },
      { t: "Currently overwhelmed", w: 0 },
    ],
  },
  {
    q: "Is your family / partner / household aligned on fostering?",
    help: "Children need an entire household that wants them there — not just one champion.",
    options: [
      { t: "Fully aligned and ready", w: 3 },
      { t: "Mostly aligned", w: 2 },
      { t: "Still in discussion", w: 1 },
      { t: "Not aligned", w: 0 },
    ],
  },
  {
    q: "Can you absorb unexpected changes in routine (meetings, court dates, school meetings, therapy)?",
    help: "Foster care has lots of appointments — yours and theirs.",
    options: [
      { t: "Yes — flexible schedule", w: 3 },
      { t: "Mostly yes, with planning", w: 2 },
      { t: "It would be hard", w: 1 },
      { t: "No flexibility right now", w: 0 },
    ],
  },
  {
    q: "How do you respond when someone pushes your buttons emotionally?",
    help: "Kids from hard places often test boundaries. Reactivity can re-traumatize.",
    options: [
      { t: "I stay calm and address it later", w: 3 },
      { t: "I pause and think before reacting", w: 2 },
      { t: "I react, then regret", w: 1 },
      { t: "I'm quick to anger", w: 0 },
    ],
  },
  {
    q: "Are you open to working with biological parents when reunification is the goal?",
    help: "Foster care's first goal is reunification. This requires cooperation, not ownership.",
    options: [
      { t: "Yes — I understand that's often the mission", w: 3 },
      { t: "Mostly yes", w: 2 },
      { t: "I'm conflicted", w: 1 },
      { t: "No, I'd only want to adopt", w: 0 },
    ],
  },
  {
    q: "Do you have a support network (friends, family, church, community) who can lend a hand?",
    help: "Foster families burn out fastest without community backing.",
    options: [
      { t: "Strong network, ready to help", w: 3 },
      { t: "Some reliable people", w: 2 },
      { t: "A few", w: 1 },
      { t: "Largely on my own", w: 0 },
    ],
  },
  {
    q: "Are you willing to learn trauma-informed parenting approaches (TBRI, attachment, etc.)?",
    help: "Standard parenting often doesn't work with kids from hard places. Learning is non-negotiable.",
    options: [
      { t: "Yes — I've already started", w: 3 },
      { t: "Yes, eager to learn", w: 2 },
      { t: "Open to it", w: 1 },
      { t: "Not really", w: 0 },
    ],
  },
  {
    q: "How do you handle grief (including a child leaving your home after placement)?",
    help: "Foster parents say goodbye. A lot. It's sacred work and sacred loss.",
    options: [
      { t: "I can grieve and keep going", w: 3 },
      { t: "Hard, but I can process it", w: 2 },
      { t: "I struggle with loss", w: 1 },
      { t: "I haven't thought about this", w: 0 },
    ],
  },
  {
    q: "Why do you want to foster?",
    help: "Your 'why' will carry you through the 3 a.m. moments. Be honest.",
    options: [
      { t: "To be a safe adult for a child who needs one", w: 3 },
      { t: "To serve my faith / community calling", w: 3 },
      { t: "To expand my family", w: 2 },
      { t: "To save a child / fix the system", w: 1 },
    ],
  },
];

function scoreBand(score, max) {
  const pct = (score / max) * 100;
  if (pct >= 80) return {
    band: "Ready to Begin",
    color: "text-fc-accent",
    icon: CheckCircle2,
    desc: "You show strong readiness across the most important dimensions. The next step is formal: attend an orientation, start the certification process, and connect with a local agency. The system needs people like you.",
    next: [
      { label: "Contact Us to Get Started", to: "/contact" },
      { label: "Explore Partner Organizations", to: "/resources" },
      { label: "See Ways to Give", to: "/get-involved" },
    ],
  };
  if (pct >= 55) return {
    band: "Ready With Some Prep",
    color: "text-fc-accent",
    icon: Heart,
    desc: "You have real capacity to foster — with a bit more preparation in one or two areas. Start with a TBRI book or trauma-informed training, strengthen your support network, and talk to foster families in your area before signing up.",
    next: [
      { label: "Browse Resources & Training", to: "/resources" },
      { label: "Read About the Work", to: "/everyday-awareness" },
      { label: "Meet the Kids", to: "/meet-the-kids" },
    ],
  };
  if (pct >= 30) return {
    band: "Not Yet — But You Can Still Help",
    color: "text-fc-ink",
    icon: AlertCircle,
    desc: "Fostering isn't the right fit today — and that's honest. The good news: there are many high-impact ways to show up for foster children that don't require placement. Respite care, mentoring, sibling-visit sponsorship, and awareness work all change lives.",
    next: [
      { label: "Ways to Give", to: "/get-involved" },
      { label: "Join the #Foster365 Challenge", to: "/everyday-awareness" },
      { label: "Support the Kids", to: "/meet-the-kids" },
    ],
  };
  return {
    band: "Start With Awareness",
    color: "text-fc-ink",
    icon: Home,
    desc: "This is a great first step — knowing where you stand is honest and powerful. Fostering may not be the right path now, but awareness is the foundation of every other act. Learn, share, and stay connected.",
    next: [
      { label: "Learn About Foster Care", to: "/everyday-awareness" },
      { label: "Browse Resources", to: "/resources" },
      { label: "Get on the Newsletter", to: "/contact" },
    ],
  };
}

export default function ReadyQuiz() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);

  // Email opt-in state
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribe, setSubscribe] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const pick = (w) => {
    const next = [...answers, w];
    setAnswers(next);
    if (step + 1 >= QUESTIONS.length) setDone(true);
    else setStep(step + 1);
  };

  const reset = () => {
    setStep(0); setAnswers([]); setDone(false);
    setEmail(""); setName(""); setEmailStatus(null); setEmailSent(false);
    window.scrollTo(0, 0);
  };

  const max = QUESTIONS.length * 3;
  const score = answers.reduce((a, b) => a + b, 0);
  const result = scoreBand(score, max);
  const percent = Math.round((score / max) * 100);

  const sendResult = async (e) => {
    e.preventDefault();
    if (!email) return;
    setEmailBusy(true); setEmailStatus(null);
    try {
      await api.post("/quiz-result", {
        email, name: name || null,
        band: result.band, score, max_score: max, percent,
        subscribe,
      });
      setEmailSent(true);
      setEmailStatus("Your readiness report is on the way.");
    } catch {
      setEmailStatus("Something went wrong. Please try again or email fostercareawareness365@gmail.com.");
    } finally { setEmailBusy(false); }
  };

  return (
    <div data-testid="ready-quiz-page">
      <SEO
        path="/am-i-ready-to-foster"
        title="Am I Ready to Foster? | 2-Minute Self-Assessment Quiz"
        description="A confidential 10-question readiness quiz for prospective foster parents. Assess emotional, practical, and lifestyle readiness — get a personalized report by email. Built by Foster Care Awareness 365."
      />
      {/* Hero */}
      <section className="border-b border-fc-border">
        <div className="container-xl pt-20 pb-14">
          <Link to="/resources" className="overline link-ul flex items-center gap-2 mb-8 text-fc-muted" data-testid="quiz-back">
            <ArrowLeft size={14} /> Resources
          </Link>
          <div className="overline text-fc-accent mb-5">Self-Assessment · 10 Questions · 2 Minutes</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] max-w-5xl">
            Are you ready to change a life — <span className="italic text-fc-accent">365 days a year?</span>
          </h1>
          <p className="mt-6 text-fc-muted text-lg max-w-3xl leading-relaxed">
            This assessment is a guide, not a gatekeeper. Your answers are not stored or sent anywhere — it runs entirely in your browser. Be honest; the point is clarity.
          </p>
        </div>
      </section>

      {/* Quiz body */}
      {!done ? (
        <section className="container-xl py-16 max-w-3xl" data-testid="quiz-body">
          {/* Progress */}
          <div className="mb-10" data-testid="quiz-progress">
            <div className="flex items-center justify-between overline text-fc-muted mb-3">
              <span>Question {step + 1} of {QUESTIONS.length}</span>
              <span>{Math.round(((step) / QUESTIONS.length) * 100)}%</span>
            </div>
            <div className="h-[2px] bg-fc-border">
              <div className="h-full bg-fc-accent transition-all duration-500" style={{ width: `${((step) / QUESTIONS.length) * 100}%` }} />
            </div>
          </div>

          <div className="fade-up" key={step}>
            <div className="font-mono text-sm text-fc-accent mb-3">Q{String(step + 1).padStart(2, "0")}</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight mb-4" data-testid="quiz-question">
              {QUESTIONS[step].q}
            </h2>
            <p className="text-fc-muted text-sm italic mb-8">{QUESTIONS[step].help}</p>

            <div className="space-y-3">
              {QUESTIONS[step].options.map((o, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(o.w)}
                  className="w-full text-left border border-fc-border bg-fc-bg hover:bg-fc-surface hover:border-fc-ink transition-all duration-200 p-5 group flex items-center justify-between gap-4"
                  data-testid={`quiz-option-${i}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="font-mono text-xs text-fc-muted group-hover:text-fc-accent transition-colors">{String.fromCharCode(65 + i)}</div>
                    <div className="text-fc-ink leading-snug">{o.t}</div>
                  </div>
                  <div className="overline opacity-0 group-hover:opacity-100 transition-opacity text-fc-accent">Select →</div>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                onClick={() => { setStep(step - 1); setAnswers(answers.slice(0, -1)); }}
                className="mt-8 overline link-ul text-fc-muted"
                data-testid="quiz-back-q"
              >
                ← Previous question
              </button>
            )}
          </div>
        </section>
      ) : (
        <section className="container-xl py-16 max-w-4xl" data-testid="quiz-result">
          <div className="border border-fc-border bg-fc-bg p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <result.icon size={24} className={result.color} />
              <div className={`overline ${result.color}`}>Your Result</div>
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] mb-6" data-testid="quiz-result-band">
              {result.band}
            </h2>
            <div className="font-mono text-sm text-fc-muted mb-8">
              Score: {score} / {max} &nbsp;·&nbsp; {Math.round((score / max) * 100)}% readiness
            </div>
            <p className="text-fc-muted leading-relaxed mb-10 text-base md:text-lg" data-testid="quiz-result-desc">
              {result.desc}
            </p>
            <div className="overline text-fc-muted mb-4">Suggested Next Steps</div>
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              {result.next.map((n, i) => (
                <Link key={i} to={n.to} className="border border-fc-border bg-fc-surface p-5 card-hover group" data-testid={`quiz-next-${i}`}>
                  <div className="font-display text-lg mb-2 group-hover:text-fc-accent transition-colors">{n.label}</div>
                  <div className="overline text-fc-muted">Open →</div>
                </Link>
              ))}
            </div>

            {/* Email opt-in */}
            <div className="border-t border-fc-border pt-8 mb-8" data-testid="quiz-email-block">
              {!emailSent ? (
                <form onSubmit={sendResult} className="grid md:grid-cols-12 gap-4 items-end">
                  <div className="md:col-span-12 flex items-center gap-2 mb-1">
                    <Mail size={16} className="text-fc-accent" />
                    <div className="overline text-fc-accent">Save Your Report</div>
                  </div>
                  <div className="md:col-span-12">
                    <h3 className="font-display text-2xl md:text-3xl leading-tight mb-2">
                      Get your readiness report by email.
                    </h3>
                    <p className="text-sm text-fc-muted">
                      We'll send a copy of this result to your inbox so you can come back to it — or share with your partner, pastor, or caseworker.
                    </p>
                  </div>
                  <div className="md:col-span-4">
                    <label className="fc-label block mb-2">Name (optional)</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="fc-input" placeholder="Your name" data-testid="quiz-email-name" />
                  </div>
                  <div className="md:col-span-5">
                    <label className="fc-label block mb-2">Email</label>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="fc-input" placeholder="you@example.com" data-testid="quiz-email-input" />
                  </div>
                  <div className="md:col-span-3">
                    <button type="submit" disabled={emailBusy} className="btn-primary w-full justify-center" data-testid="quiz-email-submit">
                      {emailBusy ? "Sending…" : "Send Report"}
                    </button>
                  </div>
                  <div className="md:col-span-12 flex items-start gap-2 mt-1">
                    <input
                      type="checkbox"
                      id="quiz-subscribe"
                      checked={subscribe}
                      onChange={(e) => setSubscribe(e.target.checked)}
                      data-testid="quiz-email-subscribe"
                      className="mt-1"
                    />
                    <label htmlFor="quiz-subscribe" className="text-sm text-fc-muted cursor-pointer">
                      Also add me to the newsletter for new kid profiles, campaign launches, and #Foster365 updates.
                    </label>
                  </div>
                  {emailStatus && <div className="md:col-span-12 overline text-fc-accent" data-testid="quiz-email-status">{emailStatus}</div>}
                </form>
              ) : (
                <div className="border border-fc-accent bg-fc-accent/5 p-6 flex items-start gap-4" data-testid="quiz-email-sent">
                  <CheckCircle2 size={22} className="text-fc-accent shrink-0 mt-1" />
                  <div>
                    <div className="font-display text-xl mb-2">Report sent.</div>
                    <p className="text-sm text-fc-muted leading-relaxed">
                      Check your inbox{email ? ` at ${email}` : ""}. {subscribe ? "You've also been added to the newsletter — welcome to the mission." : "We'll only email you this once."}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-6 border-t border-fc-border">
              <button onClick={reset} className="btn-outline" data-testid="quiz-retake">Retake the Quiz</button>
              <Link to="/contact" className="btn-primary" data-testid="quiz-contact">Start a Conversation</Link>
            </div>
          </div>

          <div className="mt-10 overline text-fc-muted text-center max-w-2xl mx-auto leading-relaxed">
            This is a guide — not a certification. Licensing requirements vary by state. Speak to a local agency for the full picture.
          </div>
        </section>
      )}
    </div>
  );
}
